import CheapestQuickestCardLabel from '@/src/components/common/srp/cheapestQuickestCardLabel/cheapestQuickestNew'
import YellowBannerPromo from '@/src/components/common/srp/yellowBannerPromo'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { useGetCheapestQuickestCard } from '@/src/hooks/srp/getCheapestQuickestCard'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { FlightCard as FlightCardType } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { extractFlightCardProps, GetFlightDataReturn } from '@/src/utils/srp/flightResultsUtil'
import { cardGrouping } from '@/src/utils/srp/searchUtils/cardGrouping'
import { Box, Button, Card, Collapse, Typography } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import ReactList from 'react-list'
import { MobileFlightCard } from '../flightCard/mobileFlightCard'

interface FlightResultsMobileProps {
    sortedFilteredCards: FlightCardType[]
    getFlightData: (flight: FlightCardType) => GetFlightDataReturn
    route?: string
    onBookFlight?: (flight: FlightCardType) => void
    onViewDetails?: (flight: FlightCardType) => void
    onSortByPrice?: () => void
    sectors: any
    tCommon: TranslationFunction<'common'>
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    limitedView?: boolean
    limitedCount?: number
    showLoadMore?: boolean
    onLoadMore?: () => void
}

const FlightResultsMobile: React.FC<FlightResultsMobileProps> = ({
    sortedFilteredCards = [],
    getFlightData,
    onBookFlight,
    sectors,
    onViewDetails,
    tFlightDetails,
    tCommon,
    limitedView = false,
    limitedCount = 5,
    showLoadMore = false,
    onLoadMore
}) => {
    const [expandedPrice, setExpandedPrice] = useState<string | null>(null)
    const groupEntries = useMemo(() => cardGrouping(sortedFilteredCards, false), [sortedFilteredCards])
    const quickestCheapestCard = useGetCheapestQuickestCard()
    const { isPerPaxPricingEnabled } = useSearchResults()

    // Handle limited view for flight schedule page
    const displayGroupEntries = useMemo(() => {
        if (limitedView) {
            return groupEntries.slice(0, limitedCount)
        }
        return groupEntries
    }, [groupEntries, limitedView, limitedCount])
    const renderExpandedSamePriceCards = useCallback(
        (extraCards: FlightCardType[]) => {
            return (
                <Box sx={{ background: '#fafbfc', borderRadius: 2, p: 1, mt: 1, px: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {tFlightDetails('samePriceFlights')}
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setExpandedPrice(null)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {tFlightDetails('hideMoreOption')}
                        </Button>
                    </Box>
                    <ReactList
                        length={extraCards.length}
                        itemRenderer={(idx, key) => {
                            const card = extraCards[idx]
                            return (
                                <ErrorBoundaryWrapper
                                    identifier={`flight-results-mobile-card-${card.id}-PWA`}
                                    fallback={null}
                                    key={key}
                                >
                                    <Card
                                        sx={{
                                            border: '1px solid #eee',
                                            borderRadius: 2,
                                            background: '#fff',
                                            mb: 2
                                        }}
                                    >
                                        <MobileFlightCard
                                            {...extractFlightCardProps(
                                                card,
                                                sectors,
                                                getFlightData,
                                                isPerPaxPricingEnabled
                                            )}
                                            onBook={() => onBookFlight?.(card)}
                                            onViewDetails={() => onViewDetails?.(card)}
                                            tFlightDetails={tFlightDetails}
                                            tCommon={tCommon}
                                        />
                                    </Card>
                                </ErrorBoundaryWrapper>
                            )
                        }}
                    />
                </Box>
            )
        },
        [getFlightData, onBookFlight, sectors, tCommon, tFlightDetails, onViewDetails]
    )

    const renderCheapestQuickestCard = useCallback(() => {
        return (
            <Box p={1.5} borderRadius={1} bgcolor={"#F3F3F3"} mb={1} display={"flex"} flexDirection={"column"} gap={1}>
                {quickestCheapestCard.map(sortOptions => (
                    <CheapestQuickestCardLabel
                        key={sortOptions.id}
                        label={sortOptions.label}
                        isActive={!!sortOptions.isActive}
                        sortOptionId={sortOptions.id}
                        isMobile={true}
                        isCombined={sortOptions.isCombined}
                    >
                        <ErrorBoundaryWrapper
                            identifier={`flight-results-mobile-card-${sortOptions?.card?.id}-PWA`}
                            fallback={null}
                        >
                            <Box>
                                <Card
                                    sx={{
                                        border: '1px solid #eee',
                                        borderRadius: 2,
                                        background: '#fff',
                                        transition: 'border 0.2s, padding 0.2s',
                                        position: 'relative'
                                    }}
                                >
                                    <Box height={"10px"}></Box>
                                    <YellowBannerPromo promos={sortOptions?.card?.promos} isMobile />
                                    {!!sortOptions?.card?.promos?.length && <Box height={"10px"}></Box>}
                                    <Box
                                        sx={{
                                            pt: (sortOptions?.card?.promos?.length || 0) > 0 ? 1 : 0
                                        }}
                                    >
                                        <MobileFlightCard
                                            {...extractFlightCardProps(
                                                sortOptions?.card!,
                                                sectors,
                                                getFlightData,
                                                isPerPaxPricingEnabled
                                            )}
                                            onBook={() => onBookFlight?.(sortOptions?.card!)}
                                            onViewDetails={() => onViewDetails?.(sortOptions?.card!)}
                                            tFlightDetails={tFlightDetails}
                                            tCommon={tCommon}
                                        />
                                    </Box>
                                </Card>
                            </Box>
                        </ErrorBoundaryWrapper>
                    </CheapestQuickestCardLabel>
                ))}
            </Box>
        )
    }, [getFlightData, onBookFlight, onViewDetails, sectors, tCommon, tFlightDetails])

    const renderMainCard = useCallback(
        (index: number, key: React.Key) => {
            const [price, group] = displayGroupEntries[index] as [string, FlightCardType[]]
            const mainCard = group[0]
            const extraCards = group.slice(1)
            const isExpanded = expandedPrice === price

            if (quickestCheapestCard?.some(sortOption => sortOption?.card?.id === mainCard.id)) {
                return null
            }

            return (
                <ErrorBoundaryWrapper
                    identifier={`flight-results-mobile-card-${mainCard.id}-PWA`}
                    fallback={null}
                    key={key}
                >
                    <Box sx={{ mb: 1 }}>
                        {/* Main Card */}
                        <Card
                            sx={{
                                border: '1px solid #eee',
                                borderRadius: 2,
                                background: '#fff',
                                cursor: extraCards.length > 0 ? 'pointer' : 'default',
                                transition: 'border 0.2s, padding 0.2s',
                                position: 'relative'
                            }}
                        >
                            <YellowBannerPromo promos={mainCard.promos} isMobile />
                            <Box sx={{ pt: mainCard.promos?.length > 0 ? 1 : 0 }}>
                                <MobileFlightCard
                                    {...extractFlightCardProps(
                                        mainCard,
                                        sectors,
                                        getFlightData,
                                        isPerPaxPricingEnabled
                                    )}
                                    onBook={() => onBookFlight?.(mainCard)}
                                    onViewDetails={() => onViewDetails?.(mainCard)}
                                    tFlightDetails={tFlightDetails}
                                    tCommon={tCommon}
                                />
                            </Box>
                        </Card>

                        {/* Expanded section: label, hide button, and extra cards */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderExpandedSamePriceCards(extraCards)}
                        </Collapse>

                        {/* Show the 'N more at same price' bar if not expanded */}
                        {!isExpanded && extraCards.length > 0 && (
                            <Box
                                onClick={() => setExpandedPrice(price)}
                                sx={{
                                    cursor: 'pointer',
                                    width: '90%',
                                    height: '23px',
                                    mx: 'auto',
                                    borderBottomLeftRadius: '12px',
                                    borderBottomRightRadius: '12px',
                                    background: '#F3F3F3',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    color: 'primary.main'
                                }}
                            >
                                +{extraCards.length} {tFlightDetails('moreOptionAvailiable')}
                            </Box>
                        )}
                    </Box>
                </ErrorBoundaryWrapper>
            )
        },
        [
            expandedPrice,
            onBookFlight,
            onViewDetails,
            sectors,
            getFlightData,
            tFlightDetails,
            tCommon,
            quickestCheapestCard,
            displayGroupEntries
        ]
    )

    return (
        <>
            {renderCheapestQuickestCard()}
            <Box px={1}>
                <ReactList length={displayGroupEntries.length} itemRenderer={renderMainCard} threshold={200} />
                {limitedView && showLoadMore && groupEntries.length > limitedCount && (
                    <Box
                        p={2}
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        // borderRadius={1.5}
                        sx={{
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)', // Safari support
                            backgroundColor: 'rgba(255, 255, 255, 0.635)',
                            // border: '1px solid #eee',
                            borderTop: '2px solid rgba(255, 255, 255, 0.2)'
                        }}
                    >
                        <Button variant="customVariantPrimary" onClick={onLoadMore}>
                            {tCommon('loadMore')}
                        </Button>
                    </Box>
                )}
            </Box>
        </>
    )
}

export { FlightResultsMobile }
